import { VIEW_AS_HEADER, type ApiErrorBody } from '@lp/shared';

/**
 * The only place the app talks to the API.
 *
 * The access token is held in memory (never localStorage — an XSS then cannot
 * walk off with a long-lived credential). Renewal happens against the
 * HttpOnly refresh cookie, and a 401 triggers exactly one refresh attempt
 * before the request is retried; concurrent 401s share that single attempt.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly reason?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** A 403 the UI should render as "locked, because…" rather than an error. */
  get isPermission(): boolean {
    return this.status === 403;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

type TokenState = {
  accessToken: string | null;
  expiresAt: number;
};

const state: TokenState = { accessToken: null, expiresAt: 0 };

let refreshInFlight: Promise<string | null> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null, expiresInSec = 0): void {
  state.accessToken = token;
  // Renew a minute early so an in-flight request never races the expiry.
  state.expiresAt = token ? Date.now() + Math.max(0, expiresInSec - 60) * 1000 : 0;
}

export function getAccessToken(): string | null {
  return state.accessToken;
}

// ------------------------------------------------------------------ view as ---

/**
 * The user an admin is viewing the platform as, sent on every API call in the
 * view-as header. Kept in sessionStorage: it survives a reload, and it stays
 * in this tab — another tab is still the admin.
 */
const VIEW_AS_KEY = 'lp.viewAs';

function readViewAs(): string | null {
  try {
    return window.sessionStorage.getItem(VIEW_AS_KEY);
  } catch {
    return null;
  }
}

let viewAsUserId: string | null = readViewAs();

export function getViewAs(): string | null {
  return viewAsUserId;
}

export function setViewAs(userId: string | null): void {
  viewAsUserId = userId;
  try {
    if (userId) window.sessionStorage.setItem(VIEW_AS_KEY, userId);
    else window.sessionStorage.removeItem(VIEW_AS_KEY);
  } catch {
    // Without storage the view lasts until the page is reloaded.
  }
}

function viewAsHeaders(): Record<string, string> {
  return viewAsUserId ? { [VIEW_AS_HEADER]: viewAsUserId } : {};
}

export function onAuthLostHandler(fn: (() => void) | null): void {
  onAuthLost = fn;
}

/** POST /auth/refresh using the cookie. Returns the new token, or null. */
export async function refreshSession(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (!res.ok) {
        setAccessToken(null);
        return null;
      }
      const data = (await res.json()) as { accessToken: string; expiresInSec: number };
      setAccessToken(data.accessToken, data.expiresInSec);
      return data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      // Cleared on the next microtask so simultaneous callers all see the
      // same promise rather than each starting their own refresh.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function ensureToken(): Promise<string | null> {
  if (state.accessToken && Date.now() < state.expiresAt) return state.accessToken;
  return refreshSession();
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip the Authorization header (used by the auth endpoints themselves). */
  anonymous?: boolean;
  /** Send the request as the admin even while viewing as someone else. */
  asSelf?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

async function toApiError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error page (nginx, timeout) — fall through to a generic message */
  }
  const err = body?.error;
  return new ApiError(
    res.status,
    err?.code ?? `http_${res.status}`,
    err?.message ?? fallbackMessage(res.status),
    err?.reason,
    err?.details,
  );
}

function fallbackMessage(status: number): string {
  if (status === 404) return 'That is not here.';
  if (status === 413) return 'The file is larger than the server accepts for this kind of upload.';
  if (status === 429) return 'Too many requests — give it a moment.';
  if (status >= 500) return 'The server had a problem. Try again shortly.';
  return 'The request could not be completed.';
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, asSelf = false, signal, headers = {} } = opts;

  const send = async (token: string | null): Promise<Response> =>
    fetch(path.startsWith('/api') ? path : `/api${path}`, {
      method,
      credentials: 'include',
      signal,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(anonymous || asSelf ? {} : viewAsHeaders()),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  const token = anonymous ? null : await ensureToken();
  let res = await send(token);

  if (res.status === 401 && !anonymous) {
    const fresh = await refreshSession();
    if (fresh) {
      res = await send(fresh);
    } else {
      onAuthLost?.();
      throw await toApiError(res);
    }
  }

  if (!res.ok) throw await toApiError(res);

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await res.text()) as T;

  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  anonymous: {
    get: <T>(path: string) => request<T>(path, { anonymous: true }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body, anonymous: true }),
  },
};

/**
 * POST that answers with server-sent events, each `data:` line handed to
 * `onEvent` parsed as JSON as soon as it arrives. EventSource cannot POST or
 * send an Authorization header, so this reads the body stream itself — with
 * the same token handling as `request`. A refusal before the stream opens
 * (401, 403, 429, validation) throws an ApiError like any other call.
 */
export async function streamEvents<E>(
  path: string,
  body: unknown,
  onEvent: (event: E) => void,
  signal?: AbortSignal,
): Promise<void> {
  const send = (token: string | null): Promise<Response> =>
    fetch(path.startsWith('/api') ? path : `/api${path}`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        accept: 'text/event-stream',
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...viewAsHeaders(),
      },
      body: JSON.stringify(body),
    });

  let res = await send(await ensureToken());
  if (res.status === 401) {
    const fresh = await refreshSession();
    if (!fresh) {
      onAuthLost?.();
      throw await toApiError(res);
    }
    res = await send(fresh);
  }
  if (!res.ok || !res.body) throw await toApiError(res);

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    // Events end with a blank line; a trailing partial event waits for more.
    let end: number;
    while ((end = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const data = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      // Comment lines (": keep-alive") carry no data.
      if (data) onEvent(JSON.parse(data) as E);
    }
  }
}

/**
 * Raw upload of a single file through the API (small assets: images, starter
 * files, interactive bundles). Large video goes straight to object storage via
 * presigned parts and never passes through here.
 */
export async function uploadFile(
  path: string,
  file: File,
  opts: { fields?: Record<string, string>; onProgress?: (pct: number) => void } = {},
): Promise<unknown> {
  const token = await ensureToken();
  const form = new FormData();
  for (const [k, v] of Object.entries(opts.fields ?? {})) form.append(k, v);
  form.append('file', file, file.name);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path.startsWith('/api') ? path : `/api${path}`);
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    for (const [k, v] of Object.entries(viewAsHeaders())) xhr.setRequestHeader(k, v);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
    });

    xhr.addEventListener('load', () => {
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText) as unknown;
      } catch {
        /* keep parsed null and fall into the status check below */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed);
      } else {
        const err = (parsed as ApiErrorBody | null)?.error;
        reject(
          new ApiError(
            xhr.status,
            err?.code ?? `http_${xhr.status}`,
            err?.message ?? fallbackMessage(xhr.status),
            err?.reason,
          ),
        );
      }
    });

    xhr.addEventListener('error', () =>
      reject(new ApiError(0, 'network_error', 'The upload could not reach the server.')),
    );
    xhr.addEventListener('abort', () =>
      reject(new ApiError(0, 'aborted', 'The upload was cancelled.')),
    );

    xhr.send(form);
  });
}
